import { Suspense, lazy, type ReactElement } from "react";
import { Navigate, Route, Routes, useLocation, useMatch, type PathPattern } from "react-router";
import { AppLayout } from "@/app/layout/app-layout";
import { PublicChromeLayout } from "@/app/layout/public-chrome-layout";
import { isInvoicesPath } from "@/app/layout/restricted-shell";
import { ADMIN_FINANCIAL_PATH_PATTERN } from "@/app/router/module-manifests";
import { ProtectedRoute } from "@/app/router/protected-route";
import { AdminRealmBlockedRoute } from "@/app/router/realm-boundary";
import { routePathById } from "@/app/router/route-manifest";
import {
  ADMIN_FINANCE_ROLE_CLAIMS,
  ADMIN_OPERATOR_ROLE_CLAIMS,
  PEOPLE_OPERATOR_ROLE_CLAIMS,
} from "@/core/auth/admin-role-tiers";
import type { ActiveAuthRealm } from "@/core/auth/realm";
import {
  AuthCallbackRoute,
  DeniedPersonRefusalRedirect,
  ErrorRoute,
  HealthRoute,
  LoginRoute,
  LogoutRoute,
} from "@/features/auth/auth-routes";
import { RegistrationInvitationAdminRealmPage } from "@/features/registration-invitation/registration-invitation-admin-realm-page";
import { RegistrationCredentialRecoveryPage } from "@/features/registration-invitation/registration-credential-recovery-page";
import {
  RegistrationInvitationPage,
  RegistrationLegacyPathPage,
} from "@/features/registration-invitation/registration-invitation-page";
import { CredentialRecoveryClientProvider } from "@/features/registration-invitation/use-credential-recovery";
import {
  FirstCredentialAdminRealmPage,
  FirstCredentialPage,
} from "@/features/first-credential/first-credential-page";
import { PayGuidanceAdminRealmPage, PayGuidancePage } from "@/features/invoices/pay-guidance-page";
import { PayLocatorLandingPage } from "@/features/invoices/pay-locator-landing-page";
import {
  AdminModulePlaceholderRoutes,
  ModulePlaceholderRoutes,
} from "@/features/placeholders/module-placeholder-routes";
import { ClmIcon } from "@/shared/components/icons/icon-map";

const DashboardPage = lazy(async () => ({
  default: (await import("@/features/dashboard/dashboard-page")).DashboardPage,
}));
const AdminDashboardPage = lazy(async () => ({
  default: (await import("@/features/admin/dashboard/admin-dashboard-page")).AdminDashboardPage,
}));
const PeopleAdminPage = lazy(async () => ({
  default: (await import("@/features/admin/people/people-admin-page")).PeopleAdminPage,
}));
const PersonCreatePage = lazy(async () => ({
  default: (await import("@/features/admin/people/person-create/person-create-page"))
    .PersonCreatePage,
}));
// 011-22 T038–T040 provider seam: mounting the provider activates the guided
// company/location picker on the three converted Admin People surfaces.
const AdminCompanyLocationsProvider = lazy(async () => ({
  default: (await import("@/features/admin/people/admin-company-locations-queries"))
    .AdminCompanyLocationsClientProvider,
}));
const PersonEditPage = lazy(async () => ({
  default: (await import("@/features/admin/people/person-edit/person-edit-page")).PersonEditPage,
}));
const PendingApplicationsPage = lazy(async () => ({
  default: (await import("@/features/admin/people/pending-applications/pending-applications-page"))
    .PendingApplicationsPage,
}));
const PendingApplicationReviewPage = lazy(async () => ({
  default: (
    await import("@/features/admin/people/pending-applications/pending-application-review-page")
  ).PendingApplicationReviewPage,
}));
const AdminIdentitiesPage = lazy(async () => ({
  default: (await import("@/features/admin/identities/admin-identities-page")).AdminIdentitiesPage,
}));
const StaffInvoiceListPage = lazy(async () => ({
  default: (await import("@/features/financial/staff-invoices/routes/staff-invoice-list"))
    .StaffInvoiceListPage,
}));
const StaffInvoiceDetailPage = lazy(async () => ({
  default: (await import("@/features/financial/staff-invoices/routes/staff-invoice-detail"))
    .StaffInvoiceDetailPage,
}));
const ContentHandoffPage = lazy(async () => ({
  default: (await import("@/features/admin/content/content-handoff-page")).ContentHandoffPage,
}));
const CONTENT_HANDOFF_PATTERN: PathPattern = {
  path: routePathById("admin-content-handoff"),
  end: true,
};
// 004-6 T060/T061: courses surfaces that obtain the bearer while mounted.
const COURSES_PATH_PATTERN: PathPattern = { path: routePathById("courses-wildcard") };
const MY_COURSES_PATTERN: PathPattern = { path: routePathById("my-courses"), end: true };
const EventsRoutes = lazy(async () => ({
  default: (await import("@/features/events/events-routes")).EventsRoutes,
}));
const UccRoutes = lazy(async () => ({
  default: (await import("@/features/ucc/ucc-routes")).UccRoutes,
}));
const CoursesRoutes = lazy(async () => ({
  default: (await import("@/features/courses/courses-routes")).CoursesRoutes,
}));
const StaffCoursesRoutes = lazy(async () => ({
  default: (await import("@/features/courses/courses-routes")).StaffCoursesRoutes,
}));
const MyCoursesPage = lazy(async () => ({
  default: (await import("@/features/courses/pages/MyCoursesPage")).MyCoursesPage,
}));
const ComplianceRoutes = lazy(async () => ({
  default: (await import("@/features/compliance/compliance-routes")).ComplianceRoutes,
}));
const InvoicesRoutes = lazy(async () => ({
  default: (await import("@/features/invoices/invoices-routes")).InvoicesRoutes,
}));
const FinancialRoutes = lazy(async () => ({
  default: (await import("@/features/financial/financial-routes")).FinancialRoutes,
}));
const DocumentsRoutes = lazy(async () => ({
  default: (await import("@/features/documents/documents-routes")).DocumentsRoutes,
}));
const SearchRoutes = lazy(async () => ({
  default: (await import("@/features/search/search-routes")).SearchRoutes,
}));
const SiteContentRoutes = lazy(async () => ({
  default: (await import("@/features/site-content/site-content-pages")).SiteContentRoutes,
}));
// 011-CLMS-241 US3: copy-only signed-in pages for legacy hosts of forms not delivered yet.
const MemberContentRoutes = lazy(async () => ({
  default: (await import("@/features/site-content/site-content-pages")).MemberContentRoutes,
}));
// 011-CLMS-241 T063: lazy so the Live Preview client stays out of the main bundle.
const CmsPreviewPage = lazy(async () => ({
  default: (await import("@/features/cms/cms-preview-page")).CmsPreviewPage,
}));

export interface AppRouterProps {
  /**
   * Active auth realm for the current host. Defaults to the standard realm;
   * the bootstrap entry point resolves the realm from runtime config and the
   * browser origin. Admin routes are only served on the admin realm host.
   */
  realm?: ActiveAuthRealm;
}

const sharedPublicAuthRoutes = [
  <Route key="login" path="/login" element={<LoginRoute />} />,
  <Route key="auth-callback" path="/auth/callback" element={<AuthCallbackRoute />} />,
  <Route key="logout" path="/logout" element={<LogoutRoute />} />,
  <Route key="error" path="/error" element={<ErrorRoute />} />,
  <Route key="health" path="/health" element={<HealthRoute />} />,
  // CLMS-113 US4 (FR-025): legacy registration paths fail closed on the
  // generic unavailable page — public, no lookup, no auth/dashboard redirect.
  // The explicit /register/invitation feature route outranks the wildcard.
  <Route
    key="registration-legacy-index"
    path="/register"
    element={<RegistrationLegacyPathPage />}
  />,
  <Route key="registration-legacy" path="/register/*" element={<RegistrationLegacyPathPage />} />,
  // T152: legacy /home/* content pages ported so the shell footer links resolve
  // on this origin (public in legacy, public here). The persistent public
  // chrome renders at the router level, outside the lazy boundary, so the
  // loading fallback appears inside branded chrome (FR-040) — the page
  // components themselves stay lazy.
  <Route key="site-content" element={<PublicChromeLayout />}>
    <Route path="/home/*" element={<SiteContentRoutes />} />
  </Route>,
];

// CLMS-113: real feature route on a minimal
// unauthenticated layout with no authenticated navigation. Served ONLY on the
// standard realm; the admin realm renders generic support/sign-out guidance
// instead (UI contract "Authentication compatibility").
const standardPublicRoutes = [
  ...sharedPublicAuthRoutes,
  <Route
    key="registration-invitation"
    path="/register/invitation"
    element={<RegistrationInvitationPage />}
  />,
  // 011-CLMS-241 FR-018: Live Preview target framed by the stage's CMS origin (CSP
  // frame-ancestors on the standard realm only); in no menu.
  <Route key="cms-preview" element={<PublicChromeLayout />}>
    <Route path="/cms-preview/:key" element={<CmsPreviewPage />} />
  </Route>,
  // 011-15 A §8.1: credential-recovery return target (standard realm only).
  <Route
    key="registration-invitation-recovery"
    path="/register/invitation/recovery"
    element={
      <CredentialRecoveryClientProvider>
        <RegistrationCredentialRecoveryPage />
      </CredentialRecoveryClientProvider>
    }
  />,
  // CLMS-137: bearer-free first-credential page; authorization arrives only
  // through the one-read in-memory handoff on the standard realm.
  <Route key="set-password" path="/set-password" element={<FirstCredentialPage />} />,
  // 006-9 T056 (UI contract §2, §3.2, §4): public invoice-locator landing,
  // post-login continuation, and neutral guidance. Static imports keep the
  // locator scrub in the first render, ahead of any lazy chunk request.
  <Route
    key="pay-locator-landing-locator"
    path="/pay/i/:locator"
    element={<PayLocatorLandingPage />}
  />,
  <Route key="pay-locator-landing" path="/pay/i" element={<PayLocatorLandingPage />} />,
  <Route key="pay-guidance" path="/pay/guidance" element={<PayGuidancePage />} />,
];

const adminPublicRoutes = [
  ...sharedPublicAuthRoutes,
  <Route
    key="registration-invitation-admin"
    path="/register/invitation"
    element={<RegistrationInvitationAdminRealmPage />}
  />,
  // 011-15 A §8.1: the admin realm never runs the recovery finalize; it
  // renders the same generic invitation guidance as the registration path.
  <Route
    key="registration-invitation-recovery-admin"
    path="/register/invitation/recovery"
    element={<RegistrationInvitationAdminRealmPage />}
  />,
  // CLMS-137: the admin realm renders generic guidance only — it never
  // claims the handoff and never calls the first-credential API.
  <Route
    key="set-password-admin"
    path="/set-password"
    element={<FirstCredentialAdminRealmPage />}
  />,
  // 006-9 UI contract §4: every /pay/* path on the admin realm renders the
  // generic guidance variant — no locator resolution, no member requests.
  <Route key="pay-admin" path="/pay" element={<PayGuidanceAdminRealmPage />} />,
  <Route key="pay-wildcard-admin" path="/pay/*" element={<PayGuidanceAdminRealmPage />} />,
];

/**
 * Standard-realm layout guard. 006-9 UI contract §0 (no-unmount): on the
 * `/pay/invoices` surfaces and My Courses the layout stays mounted while the
 * session is `refreshing` so the nested `keepMountedWhileRefreshing` route
 * guard is reachable; that nested guard owns the single inline cue. Every other
 * route keeps the route-level "Checking session" behaviour.
 */
function StandardLayoutGuard(): ReactElement {
  const { pathname } = useLocation();
  const isMyCourses = useMatch(MY_COURSES_PATTERN) !== null;
  const keepMounted = isInvoicesPath(pathname) || isMyCourses;
  return (
    <ProtectedRoute
      requiredRoles={["authenticated"]}
      keepMountedWhileRefreshing={keepMounted}
      refreshCue="none"
    >
      <AppLayout />
    </ProtectedRoute>
  );
}

// 006-6 T165: Admin People surfaces that obtain a financial bearer while
// mounted (the pricing override on Create person and in the reissue dialog).
const ADMIN_PEOPLE_CREATE_PATTERN: PathPattern = { path: "/admin/people/new", end: true };
const ADMIN_PEOPLE_EDIT_PATTERN: PathPattern = { path: "/admin/people/:personId/edit", end: true };

/**
 * Admin-realm layout guard: the §0 no-unmount rule above, applied to the
 * staff financial surfaces under the admin financial module route base, to
 * the CMS hand-off, which obtains the bearer inside the mounted page, to
 * the Admin People surfaces that save a pricing override, and to the courses
 * module (the LMS-events review).
 */
function AdminLayoutGuard(): ReactElement {
  // useMatch uses the routes' decoded, case-insensitive pathname, so the hand-off and financial surfaces stay mounted.
  const isAdminFinancial = useMatch(ADMIN_FINANCIAL_PATH_PATTERN) !== null;
  const isContentHandoff = useMatch(CONTENT_HANDOFF_PATTERN) !== null;
  const isPeopleCreate = useMatch(ADMIN_PEOPLE_CREATE_PATTERN) !== null;
  const isPeopleEdit = useMatch(ADMIN_PEOPLE_EDIT_PATTERN) !== null;
  const isCourses = useMatch(COURSES_PATH_PATTERN) !== null;
  return (
    <ProtectedRoute
      requiredRoles={["authenticated"]}
      keepMountedWhileRefreshing={
        isAdminFinancial || isContentHandoff || isPeopleCreate || isPeopleEdit || isCourses
      }
      refreshCue="none"
    >
      <AppLayout />
    </ProtectedRoute>
  );
}

export function AppRouter({ realm = "standard" }: AppRouterProps = {}): ReactElement {
  if (realm === "admin") {
    return <AdminRealmRouter />;
  }

  // The auth service refuses only standard-realm sessions; the admin realm never shows a refusal.
  return (
    <>
      <StandardRealmRouter />
      {/* After the routes: its navigation runs last, over a guard's redirect to the plain login. */}
      <DeniedPersonRefusalRedirect />
    </>
  );
}

function StandardRealmRouter(): ReactElement {
  return (
    <Routes>
      {standardPublicRoutes}
      <Route element={<StandardLayoutGuard />}>
        <Route
          index
          element={
            <LazyRouteElement>
              <DashboardPage />
            </LazyRouteElement>
          }
        />
        <Route
          path="dashboard"
          element={
            <LazyRouteElement>
              <DashboardPage />
            </LazyRouteElement>
          }
        />
        <Route path="admin/*" element={<AdminRealmBlockedRoute />} />
        <Route
          path="events/*"
          element={
            <ProtectedRoute requiredRoles={["events:read"]}>
              <LazyRouteElement>
                <EventsRoutes />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="ucc/*"
          element={
            <ProtectedRoute requiredRoles={["ucc:read"]}>
              <LazyRouteElement>
                <UccRoutes />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        {/* 004-6 T048/T049: learner catalog and My Courses for members; the
            staff permission still admits (D17) but is not required here. */}
        <Route
          path="courses/*"
          element={
            <ProtectedRoute requiredRoles={["member", "courses:view"]}>
              <LazyRouteElement>
                <CoursesRoutes />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        {/* 006-9 UI contract §0: the certificate download obtains the bearer
            while mounted, so My Courses stays mounted through a refresh. */}
        <Route
          path="my-courses"
          element={
            <ProtectedRoute requiredRoles={["member"]} keepMountedWhileRefreshing>
              <LazyRouteElement>
                <MyCoursesPage />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="compliance/*"
          element={
            <ProtectedRoute requiredRoles={["compliance:read"]}>
              <LazyRouteElement>
                <ComplianceRoutes />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="financial/*"
          element={
            <ProtectedRoute requiredRoles={["financial:read"]}>
              <LazyRouteElement>
                <FinancialRoutes />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="documents/*"
          element={
            <ProtectedRoute requiredRoles={["documents:read"]}>
              <LazyRouteElement>
                <DocumentsRoutes />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="search/*"
          element={
            <ProtectedRoute requiredRoles={["search:read"]}>
              <LazyRouteElement>
                <SearchRoutes />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        {/* 006-9 UI contract §0: payment surfaces stay mounted through a
            token refresh so in-flight attempt state is never lost. */}
        <Route
          path="pay/invoices/*"
          element={
            <ProtectedRoute requiredRoles={["authenticated"]} keepMountedWhileRefreshing>
              <LazyRouteElement>
                <InvoicesRoutes />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        {/* 011-CLMS-241 US3: member-only copy pages; the static prefix outranks the member module
            placeholder below, which keeps every other /member path. */}
        <Route
          path="member/content/*"
          element={
            <ProtectedRoute requiredRoles={["member"]}>
              <LazyRouteElement>
                <MemberContentRoutes />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        {ModulePlaceholderRoutes}
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function AdminRealmRouter(): ReactElement {
  return (
    <Routes>
      {adminPublicRoutes}
      <Route element={<AdminLayoutGuard />}>
        <Route index element={<Navigate to="/admin" replace />} />
        {/* T053: /admin lands on the manifest-driven Admin dashboard; the
            module placeholder still catches other admin subpaths. Every
            operator tier (including Courses Admin, D17) is admitted to the
            realm and its landing, so the landing accepts the shared operator
            claims; People/adjudication routes accept only the People operator
            claims (Courses Admin holds no People permissions). Both are
            matched literally, mirroring the server rule that escalation
            modifiers (owner/super_admin) never admit on their own; identities
            stays admin-gated (CLMS-116 T059). */}
        <Route
          path="admin"
          element={
            <ProtectedRoute requiredRoles={ADMIN_OPERATOR_ROLE_CLAIMS} roleMatch="literal">
              <LazyRouteElement>
                <AdminDashboardPage />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/people"
          element={
            <ProtectedRoute requiredRoles={PEOPLE_OPERATOR_ROLE_CLAIMS} roleMatch="literal">
              <LazyRouteElement>
                <PeopleAdminPage />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/people/new"
          element={
            <ProtectedRoute
              requiredRoles={PEOPLE_OPERATOR_ROLE_CLAIMS}
              roleMatch="literal"
              keepMountedWhileRefreshing
            >
              <LazyRouteElement>
                <AdminCompanyLocationsProvider>
                  <PersonCreatePage />
                </AdminCompanyLocationsProvider>
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/people/:personId/edit"
          element={
            <ProtectedRoute
              requiredRoles={PEOPLE_OPERATOR_ROLE_CLAIMS}
              roleMatch="literal"
              keepMountedWhileRefreshing
            >
              <LazyRouteElement>
                <AdminCompanyLocationsProvider>
                  <PersonEditPage />
                </AdminCompanyLocationsProvider>
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/people/pending-applications"
          element={
            <ProtectedRoute requiredRoles={PEOPLE_OPERATOR_ROLE_CLAIMS} roleMatch="literal">
              <LazyRouteElement>
                <PendingApplicationsPage />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/people/pending-applications/:applicationId"
          element={
            <ProtectedRoute requiredRoles={PEOPLE_OPERATOR_ROLE_CLAIMS} roleMatch="literal">
              <LazyRouteElement>
                <AdminCompanyLocationsProvider>
                  <PendingApplicationReviewPage />
                </AdminCompanyLocationsProvider>
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/identities"
          element={
            // Literal match: identities read/list is gated on the literal
            // Admin claim server-side, so hasRole's owner/super_admin
            // expansion must not admit here (it would 403 on the list read).
            <ProtectedRoute requiredRoles={["admin"]} roleMatch="literal">
              <LazyRouteElement>
                <AdminIdentitiesPage />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        {/* D17: Courses Admin is an admin-realm operator tier, so the Courses
            module (courses:view) is served from the admin realm as well as the
            standard realm instead of falling through to the /admin redirect
            (manifest `realms`, which also drives the Admin dashboard tile).
            006-9 UI contract §0: the LMS-events review obtains the bearer while
            mounted, so it stays mounted through a refresh. */}
        <Route
          path="courses/*"
          element={
            <ProtectedRoute requiredRoles={["courses:view"]} keepMountedWhileRefreshing>
              <LazyRouteElement>
                <StaffCoursesRoutes />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        {/* 006-9 UI contract §8a: staff invoice surfaces of the admin-realm
            financial module (ADMIN_FINANCIAL_MODULE_MANIFEST). */}
        <Route
          path="admin/financial/invoices"
          element={
            <ProtectedRoute
              requiredRoles={ADMIN_FINANCE_ROLE_CLAIMS}
              roleMatch="literal"
              keepMountedWhileRefreshing
            >
              <LazyRouteElement>
                <StaffInvoiceListPage />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/financial/invoices/:invoiceId"
          element={
            <ProtectedRoute
              requiredRoles={ADMIN_FINANCE_ROLE_CLAIMS}
              roleMatch="literal"
              keepMountedWhileRefreshing
            >
              <LazyRouteElement>
                <StaffInvoiceDetailPage />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        {/* 011-CLMS-237: CMS session hand-off for any operator tier (the CMS
            decides the editor role); the page owns its loading cue and stays
            mounted while the fresh-token retry refreshes the session. */}
        <Route
          path="admin/content/handoff"
          element={
            <ProtectedRoute
              requiredRoles={ADMIN_OPERATOR_ROLE_CLAIMS}
              roleMatch="literal"
              keepMountedWhileRefreshing
              refreshCue="none"
            >
              <LazyRouteElement>
                <ContentHandoffPage />
              </LazyRouteElement>
            </ProtectedRoute>
          }
        />
        {AdminModulePlaceholderRoutes}
      </Route>
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

function LazyRouteElement({ children }: { children: ReactElement }): ReactElement {
  return (
    <Suspense fallback={<RouteLoadingFallback label="Loading module route" />}>{children}</Suspense>
  );
}

function RouteLoadingFallback({ label }: { label: string }): ReactElement {
  return (
    // Not a <main>: renders inside AppLayout's main landmark via ResponsiveSidebar.
    <div className="clm-page-gutter clm-section-spacing min-h-screen bg-background text-foreground">
      <div
        className="mx-auto flex max-w-3xl items-center gap-2 rounded-lg border bg-card p-8 text-sm text-muted-foreground shadow-sm"
        role="status"
      >
        <ClmIcon name="loading" decorative className="h-6 w-6 animate-spin text-clm-brand-blue" />
        <span>{label}</span>
      </div>
    </div>
  );
}
